import { useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth';
import { ApiError, confirmConsentChoices, getAuthToken, getConsentChoices, observeAuthTokenChanges, withdrawConsentChoice } from '@/lib/api';
import { unscopedKey } from '@/lib/queryClient';
import type { ConsentChoiceRecord, ConsentChoicesCatalogue, ConsentChoicesRequest } from '@/lib/types';
import { consentCommandRevision, freezeChoices, notifyConsentCommands, rememberedCommand, rejectedChoices, subscribeConsentCommands, validateConsentReceipt } from './consentCommands';

export function consentOwner() {
  const s = useAuthStore.getState();
  return JSON.stringify([s.user?.id, s.currentClub?.id ?? null, s.me?.host_kind, s.hydrating, !!s.me,
    typeof window === 'undefined' ? '' : window.location.host]);
}
export function useConsentChoices(invitationToken?: string) {
  const client = useQueryClient();
  useSyncExternalStore(subscribeConsentCommands, consentCommandRevision);
  const identity = useAuthStore(s => `${s.user?.id ?? ''}:${s.currentClub?.id ?? ''}:${s.hydrating}:${!!s.me}:${s.me?.host_kind ?? ''}`);
  const authority = consentOwner();
  const owner = authority + ':' + (invitationToken ?? 'account');
  const [epoch, setEpoch] = useState(0);
  const [data, setData] = useState<ConsentChoicesCatalogue | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [, redraw] = useState(0);
  const lifetime = useRef<AbortController | null>(null);
  const liveToken = useRef(getAuthToken());
  const command = rememberedCommand(owner);
  const ready = !!useAuthStore.getState().user && !!useAuthStore.getState().me && !useAuthStore.getState().hydrating;
  const current = useCallback(() => consentOwner() === authority && getAuthToken() === liveToken.current && !lifetime.current?.signal.aborted, [authority]);
  useLayoutEffect(() => {
    const unsubscribe = observeAuthTokenChanges(() => { lifetime.current?.abort(); setData(null); setSelected(new Set()); setBusy(false); setStatus(''); setEpoch(n => n + 1); });
    return unsubscribe;
  }, []);
  useLayoutEffect(() => { setBusy(false); setStatus(''); }, [owner]);
  useEffect(() => {
    const controller = new AbortController(); lifetime.current = controller; liveToken.current = getAuthToken();
    setData(null); setLoading(true); setError(''); setSelected(new Set());
    if (!ready) { setLoading(false); return () => controller.abort(); }
    void getConsentChoices(invitationToken, controller.signal).then(result => {
      if (controller.signal.aborted || !current()) return;
      const auth = useAuthStore.getState();
      if (result.user_id !== auth.user?.id || result.club_id !== (auth.currentClub?.id ?? null)) throw new Error('Consent owner changed');
      setData(result); setLoading(false);
    }).catch(() => { if (!controller.signal.aborted && current()) { setError('Could not load your choices. Your saved records have not changed.'); setLoading(false); } });
    return () => controller.abort();
  }, [owner, identity, epoch, invitationToken, current, ready]);
  const refresh = () => { if (!busy && !command?.pending && !command?.uncertain) setEpoch(n => n + 1); };
  const invalidate = async () => { await Promise.all(['me', 'me-consents', 'accessible-teams'].map(key => client.invalidateQueries({ queryKey: unscopedKey(key) }))); };
  const auth = useAuthStore.getState();
  const ownedData = current() && data?.user_id === auth.user?.id && data?.club_id === (auth.currentClub?.id ?? null) ? data : null;
  async function submit(legacyFallback?: ConsentChoicesRequest['legacy_fallback']) {
    if (!current() || (!ownedData && !command) || busy || command?.pending) return;
    const captured = command && !command.receipt ? command : freezeChoices(owner, ownedData!, selected, invitationToken, legacyFallback);
    if (captured.pending || captured.receipt) return;
    const requestSignal = lifetime.current?.signal;
    const requestToken = getAuthToken();
    const requestCurrent = () => current() && !requestSignal?.aborted && getAuthToken() === requestToken;
    captured.pending = true; notifyConsentCommands(); setBusy(true); setError(''); setStatus('');
    try {
      const result = validateConsentReceipt(captured, await confirmConsentChoices(captured.body, requestSignal));
      captured.receipt = result; captured.uncertain = false; notifyConsentCommands();
      if (!requestCurrent()) return;
      setSelected(new Set()); setStatus(result.joined ? captured.body.legacy_fallback ? 'You joined the team. Your team-only confirmation was saved.' : result.outcomes.length ? 'You joined the team. Your choices were saved.' : 'You joined the team. Your existing choices are unchanged.' : 'Your choices were saved.');
      // The server receipt already proves acceptance. Cache/read failures
      // cannot relabel that result or dispatch the frozen operation again.
      try {
        await invalidate();
        if (requestCurrent() && !result.joined) setEpoch(n => n + 1);
      } catch {
        if (requestCurrent()) { setData(null); setError('Your confirmation was accepted. Reload to refresh your saved records.'); }
      }
    } catch (failure) {
      rejectedChoices(owner, captured, failure);
      if (requestCurrent() && !captured.uncertain) setData(null);
      if (requestCurrent()) setError(captured.uncertain ? 'The result is unknown. Check the original request before making new choices.'
        : failure instanceof ApiError && failure.status === 409 ? 'The choices or subject changed. Reload and review before confirming.' : 'The choices were not accepted. Reload and review before confirming.');
    } finally {
      captured.pending = false; notifyConsentCommands();
      if (requestCurrent()) { setBusy(false); redraw(n => n + 1); }
    }
  }
  async function withdraw(record: ConsentChoiceRecord) {
    if (!current() || busy || command?.uncertain || command?.pending) return;
    const requestSignal = lifetime.current?.signal;
    const requestToken = getAuthToken();
    const requestCurrent = () => current() && !requestSignal?.aborted && getAuthToken() === requestToken;
    setBusy(true);setError('');
    try {
      const result = await withdrawConsentChoice({ record_id: record.id, record_kind: record.kind }, requestSignal);
      if (!requestCurrent()) return;
      setData(result);setSelected(new Set());setStatus('Choice updated. The selected permission was withdrawn.');
      try { await invalidate(); }
      catch { if (requestCurrent()) setError('Your withdrawal was confirmed. Reload to refresh other views.'); }
    } catch { if (requestCurrent()) { setData(null);setError('Withdrawal could not be confirmed. Reload your records before trying again.'); } }
    finally { if (requestCurrent()) setBusy(false); }
  }
  return { owner, data: ownedData, selected, error, status, loading, busy: busy || !!command?.pending, command, refresh, submit, withdraw,
    choose(key: string, checked: boolean) { if (!busy && !command?.pending && !command?.uncertain && current()) setSelected(previous => { const next = new Set(previous); checked ? next.add(key) : next.delete(key); return next; }); } };
}
